import { ReactNode } from 'react';

import { authApi } from '@auth/api/auth';
import { AuthContext } from '@auth/contexts/AuthContext';
import { useAuthStatus } from '@auth/hooks/useAuthStatus';
import { clearAuthStorage } from '@core/api/axiosConfig';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider component handles authentication state management and operations.
 * It provides login, logout functionality and user state to the application through AuthContext.
 *
 * Authentication flow:
 * - Manages token storage and user session
 * - Provides user data to child components
 * - Handles login/logout operations
 */
export default function AuthProvider({ children }: AuthProviderProps) {
  const { data: user, isLoading, refetch } = useAuthStatus();

  /**
   * Handles user login by storing the authentication token and refreshing the user state.
   * Token validation and session management is handled by axios interceptors.
   *
   * @param token - The authentication token received from the server
   */
  const login = async (token: string): Promise<void> => {
    localStorage.setItem('token', token);
    refetch();
  };

  /**
   * Handles user logout by:
   * 1. Calling the logout API endpoint
   * 2. Clearing authentication storage
   * 3. Refreshing the user state
   *
   * If the API call fails, it still clears local storage and refreshes state
   * to ensure the user is properly logged out client-side.
   */
  const logout = async (): Promise<void> => {
    const token = localStorage.getItem('token');
    try {
      await authApi.logout(token ?? '');
      clearAuthStorage();
      refetch();
    } catch {
      clearAuthStorage();
      refetch();
    }
  };

  return (
    <AuthContext
      value={{
        user: user ?? null,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext>
  );
}
